import { atom, type Atom } from "xoid";

// Kiosk live talk: an admin on the shop-support page speaks and it plays live
// from this kiosk's speaker. cash-agent tells us about the session over the
// `GET /kiosk-events` SSE stream; the audio comes from go2rtc through a
// recvonly RTCPeerConnection negotiated via the backend's
// `POST /kiosk-talk/:sessionId/listen`. Spec: docs/kiosk-live-talk-handoff.md.
//
// Independent of kiosk-audio on purpose: its own <audio> element, ignores the
// customer mute, and may overlap clips.

export const TALK_KEEPALIVE_TIMEOUT_MS = 30_000;
const ICE_GATHERING_TIMEOUT_MS = 2_000;
const LEVEL_SAMPLE_MS = 100;
const RECONNECT_DELAYS_MS = [1_000, 2_000, 5_000, 10_000];

/** The parts of EventSource the controller uses. */
export type TalkEventSource = {
  readonly readyState: number;
  onopen: ((event: Event) => void) | null;
  onerror: ((event: Event) => void) | null;
  addEventListener: (type: string, listener: (event: MessageEvent) => void) => void;
  close: () => void;
};

/** The parts of RTCPeerConnection the controller uses. */
export type TalkPeerConnection = Pick<
  RTCPeerConnection,
  | "addTransceiver"
  | "createOffer"
  | "setLocalDescription"
  | "setRemoteDescription"
  | "localDescription"
  | "iceGatheringState"
  | "connectionState"
  | "addEventListener"
  | "removeEventListener"
  | "close"
>;

export type TalkAudio = {
  srcObject: MediaProvider | null;
  play: () => Promise<void>;
  pause: () => void;
};

export type TalkLevelMeter = { level: () => number; close: () => void };

/** `null` when the backend says the session is over (404): ends quietly. */
export type TalkListen = (sessionId: string, sdp: string) => Promise<{ sdp: string } | null>;

export type KioskTalkDeps = {
  eventsUrl: () => string;
  listen: TalkListen;
  playChime?: () => void;
  createEventSource?: (url: string) => TalkEventSource;
  createPeerConnection?: () => TalkPeerConnection;
  createAudio?: () => TalkAudio;
  createLevelMeter?: (stream: MediaStream) => TalkLevelMeter;
  keepaliveTimeoutMs?: number;
  iceGatheringTimeoutMs?: number;
  reconnectDelaysMs?: number[];
};

export type KioskTalkController = {
  /** Opens the event stream (idempotent). It reconnects by itself until `disconnect`. */
  connect: () => void;
  disconnect: () => void;
  /** Session currently playing (banner shown), or null. */
  session: Atom<{ sessionId: string } | null>;
  /** Incoming audio level, 0..1, sampled ~10×/s while a session plays. */
  level: Atom<number>;
};

type Session = {
  sessionId: string;
  pc: TalkPeerConnection | null;
  audio: TalkAudio | null;
  meter: TalkLevelMeter | null;
  keepaliveTimer: ReturnType<typeof setTimeout> | null;
  levelTimer: ReturnType<typeof setInterval> | null;
};

const parseSessionId = (event: MessageEvent): string | null => {
  try {
    const data: unknown = JSON.parse(String(event.data));
    return typeof data === "object" && data !== null && "sessionId" in data && typeof data.sessionId === "string"
      ? data.sessionId
      : null;
  } catch {
    return null;
  }
};

const waitForIceGathering = (pc: TalkPeerConnection, timeoutMs: number) =>
  pc.iceGatheringState === "complete"
    ? Promise.resolve()
    : new Promise<void>((resolve) => {
        const done = () => {
          clearTimeout(timer);
          pc.removeEventListener("icegatheringstatechange", onChange);
          resolve();
        };
        const onChange = () => {
          if (pc.iceGatheringState === "complete") done();
        };
        // go2rtc takes one non-trickle offer; send whatever we have after the timeout.
        const timer = setTimeout(done, timeoutMs);
        pc.addEventListener("icegatheringstatechange", onChange);
      });

/** RMS of the remote stream through an AnalyserNode. */
export function createAnalyserLevelMeter(stream: MediaStream): TalkLevelMeter {
  const context = new AudioContext();
  const analyser = context.createAnalyser();
  analyser.fftSize = 512;
  context.createMediaStreamSource(stream).connect(analyser);
  const buffer = new Uint8Array(analyser.fftSize);
  return {
    level: () => {
      analyser.getByteTimeDomainData(buffer);
      const sumSquares = buffer.reduce((sum, value) => sum + ((value - 128) / 128) ** 2, 0);
      // Speech RMS rarely exceeds ~0.3; stretch it into 0..1.
      return Math.min(1, Math.sqrt(sumSquares / buffer.length) * 4);
    },
    close: () => void context.close().catch(() => {}),
  };
}

export function createKioskTalkController({
  eventsUrl,
  listen,
  playChime = () => {},
  createEventSource = (url) => new EventSource(url),
  createPeerConnection = () => new RTCPeerConnection(),
  createAudio = () => new Audio(),
  createLevelMeter = createAnalyserLevelMeter,
  keepaliveTimeoutMs = TALK_KEEPALIVE_TIMEOUT_MS,
  iceGatheringTimeoutMs = ICE_GATHERING_TIMEOUT_MS,
  reconnectDelaysMs = RECONNECT_DELAYS_MS,
}: KioskTalkDeps): KioskTalkController {
  const session = atom<{ sessionId: string } | null>(null);
  const level = atom(0);
  const state: {
    source: TalkEventSource | null;
    reconnectTimer: ReturnType<typeof setTimeout> | null;
    reconnectAttempt: number;
    current: Session | null;
  } = { source: null, reconnectTimer: null, reconnectAttempt: 0, current: null };

  const end = (target: Session, reason: string) => {
    if (state.current !== target) return;
    state.current = null;
    console.log("kiosk-talk: session ended", { sessionId: target.sessionId, reason });
    if (target.keepaliveTimer) clearTimeout(target.keepaliveTimer);
    if (target.levelTimer) clearInterval(target.levelTimer);
    target.meter?.close();
    if (target.audio) {
      target.audio.pause();
      target.audio.srcObject = null;
    }
    target.pc?.close();
    session.set(null);
    level.set(0);
  };

  const armKeepalive = (target: Session) => {
    if (target.keepaliveTimer) clearTimeout(target.keepaliveTimer);
    target.keepaliveTimer = setTimeout(() => end(target, "keepalive_timeout"), keepaliveTimeoutMs);
  };

  const playStream = (target: Session, stream: MediaStream) => {
    if (state.current !== target || target.audio) return;
    const audio = createAudio();
    target.audio = audio;
    audio.srcObject = stream;
    audio.play().catch((error) => console.error("kiosk-talk: audio play failed", error));
    try {
      const meter = createLevelMeter(stream);
      target.meter = meter;
      target.levelTimer = setInterval(() => level.set(meter.level()), LEVEL_SAMPLE_MS);
    } catch (error) {
      console.warn("kiosk-talk: level meter unavailable", error);
    }
  };

  const negotiate = async (target: Session) => {
    const pc = createPeerConnection();
    target.pc = pc;
    pc.addEventListener("connectionstatechange", () => {
      if (pc.connectionState === "failed" || pc.connectionState === "closed") {
        end(target, `peer_${pc.connectionState}`);
      }
    });
    pc.addEventListener("track", (event) => {
      const { streams, track } = event as RTCTrackEvent;
      playStream(target, streams[0] ?? new MediaStream([track]));
    });
    pc.addTransceiver("audio", { direction: "recvonly" });
    await pc.setLocalDescription(await pc.createOffer());
    await waitForIceGathering(pc, iceGatheringTimeoutMs);
    if (state.current !== target) return;
    const answer = await listen(target.sessionId, pc.localDescription?.sdp ?? "");
    if (state.current !== target) return;
    if (!answer) {
      end(target, "listen_not_found");
      return;
    }
    await pc.setRemoteDescription({ type: "answer", sdp: answer.sdp });
  };

  const onTalkStart = (sessionId: string) => {
    if (state.current?.sessionId === sessionId) {
      armKeepalive(state.current);
      return;
    }
    if (state.current) end(state.current, "replaced");
    const target: Session = {
      sessionId,
      pc: null,
      audio: null,
      meter: null,
      keepaliveTimer: null,
      levelTimer: null,
    };
    state.current = target;
    console.log("kiosk-talk: session started", { sessionId });
    session.set({ sessionId });
    armKeepalive(target);
    try {
      playChime();
    } catch (error) {
      console.warn("kiosk-talk: chime failed", error);
    }
    negotiate(target).catch((error) => {
      console.error("kiosk-talk: connecting failed", error);
      end(target, "connect_failed");
    });
  };

  const onEvent = (handler: (sessionId: string) => void) => (event: MessageEvent) => {
    const sessionId = parseSessionId(event);
    if (sessionId) handler(sessionId);
  };

  const scheduleReconnect = () => {
    if (state.reconnectTimer) return;
    const delay = reconnectDelaysMs[Math.min(state.reconnectAttempt, reconnectDelaysMs.length - 1)] ?? 5_000;
    state.reconnectAttempt += 1;
    state.reconnectTimer = setTimeout(() => {
      state.reconnectTimer = null;
      open();
    }, delay);
  };

  const open = () => {
    const source = createEventSource(eventsUrl());
    state.source = source;
    source.onopen = () => {
      state.reconnectAttempt = 0;
    };
    // EventSource retries network drops itself; it gives up (CLOSED) on a bad
    // response, e.g. while cash-agent restarts behind a proxy. Then we retry.
    source.onerror = () => {
      if (state.source !== source || source.readyState !== EventSource.CLOSED) return;
      source.close();
      state.source = null;
      scheduleReconnect();
    };
    source.addEventListener("talk-start", onEvent(onTalkStart));
    source.addEventListener(
      "talk-keepalive",
      onEvent((sessionId) => {
        if (state.current?.sessionId === sessionId) armKeepalive(state.current);
      }),
    );
    source.addEventListener(
      "talk-stop",
      onEvent((sessionId) => {
        if (state.current?.sessionId === sessionId) end(state.current, "stopped");
      }),
    );
  };

  return {
    connect: () => {
      if (state.source || state.reconnectTimer) return;
      open();
    },
    disconnect: () => {
      if (state.reconnectTimer) clearTimeout(state.reconnectTimer);
      state.reconnectTimer = null;
      state.reconnectAttempt = 0;
      state.source?.close();
      state.source = null;
      if (state.current) end(state.current, "disconnected");
    },
    session,
    level,
  };
}
